import React from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown, Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SearchDropdown } from '@/components/SearchDropdown';
import type { HealthBand } from '@/lib/api/accounts';

export interface AccountsFilterState {
  search: string;
  health: HealthBand[];
  minUsers?: number;
  datePreset: string;
}

interface AccountsFiltersProps {
  filters: AccountsFilterState;
  onFiltersChange: (patch: Partial<AccountsFilterState>) => void;
  onReset: () => void;
  onExportCsv: () => void;
  isExporting?: boolean;
}

const healthOptions: { label: string; value: HealthBand }[] = [
  { label: 'Hot', value: 'hot' },
  { label: 'Warm', value: 'warm' },
  { label: 'Neutral', value: 'neutral' },
];

const minUsersOptions: { label: string; value: string }[] = [
  { label: 'All accounts', value: '' },
  { label: '2+ users', value: '2' },
  { label: '5+ users', value: '5' },
  { label: '10+ users', value: '10' },
];

const datePresetOptions = [
  { label: 'Last 7 Days', value: 'last_7_days' },
  { label: 'Last 30 Days', value: 'last_30_days' },
  { label: 'Last 90 Days', value: 'last_90_days' },
  { label: 'All Time', value: 'all_time' },
];

function SingleSelectFilter({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { label: string; value: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const current = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 shrink-0 justify-between rounded-md border border-border/80 bg-background px-2.5 text-[13px] text-muted-foreground shadow-none hover:bg-accent hover:text-accent-foreground"
        >
          <span className="truncate text-left">{current?.label ?? label}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-2" align="start">
        <div className="flex flex-col gap-1">
          {options.map(({ label: optLabel, value: optValue }) => (
            <button
              key={String(optValue)}
              type="button"
              className={cn(
                'flex cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm text-left hover:bg-accent',
                value === optValue && 'font-medium bg-accent',
              )}
              onClick={() => {
                onChange(optValue);
                setOpen(false);
              }}
            >
              {optLabel}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: { label: string; value: HealthBand }[];
  selected: HealthBand[];
  onChange: (values: HealthBand[]) => void;
}) {
  const toggle = (value: HealthBand) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const triggerLabel =
    selected.length === 0
      ? `All ${label}`
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label ?? selected[0])
        : `${label} (${selected.length})`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 shrink-0 justify-between rounded-md border border-border/80 bg-background px-2.5 text-[13px] text-muted-foreground shadow-none hover:bg-accent hover:text-accent-foreground"
        >
          <span className="truncate text-left">{triggerLabel}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-2" align="start">
        <div className="flex flex-col gap-1">
          {options.map(({ label: optLabel, value }) => (
            <label
              key={value}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
            >
              <Checkbox
                checked={selected.includes(value)}
                onCheckedChange={() => toggle(value)}
              />
              {optLabel}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const AccountsFilters: React.FC<AccountsFiltersProps> = ({
  filters,
  onFiltersChange,
  onReset,
  onExportCsv,
  isExporting,
}) => {
  const hasActiveFilters =
    filters.search.length > 0 ||
    filters.health.length > 0 ||
    filters.minUsers !== undefined ||
    filters.datePreset !== 'last_30_days';

  return (
    <div className="flex w-full flex-wrap items-center gap-2 rounded-lg border border-border/80 bg-card px-2.5 py-2">
      <span className="mr-1 text-[11px] font-medium uppercase tracking-normal text-muted-foreground">Filters</span>

      <div className="hidden h-4 w-px bg-border sm:block" />

      <SingleSelectFilter
        label="Last 30 Days"
        options={datePresetOptions}
        value={filters.datePreset}
        onChange={(value) => onFiltersChange({ datePreset: value || 'last_30_days' })}
      />

      <MultiSelectFilter
        label="Health"
        options={healthOptions}
        selected={filters.health}
        onChange={(values) => onFiltersChange({ health: values })}
      />

      <SingleSelectFilter
        label="Users"
        options={minUsersOptions}
        value={filters.minUsers !== undefined ? String(filters.minUsers) : ''}
        onChange={(value) => onFiltersChange({ minUsers: value ? Number(value) : undefined })}
      />

      <div className="shrink-0">
        <SearchDropdown
          value={filters.search}
          onChange={(value) => onFiltersChange({ search: value })}
          placeholder="Search company or domain..."
          buttonClassName="h-8 rounded-md border border-border/80 bg-background px-2.5 text-[13px] text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          iconClassName="text-icon"
        />
      </div>

      <div className="ml-auto flex items-center gap-2">
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-xs text-muted-foreground hover:text-heading"
            onClick={onReset}
          >
            Clear
          </Button>
        )}

        <Button
          variant="outline"
          size="sm"
          className="h-8 rounded-md px-2.5 text-[13px]"
          onClick={onExportCsv}
          disabled={isExporting}
        >
          <Download className="mr-1.5 h-3.5 w-3.5" />
          {isExporting ? 'Exporting...' : 'CSV'}
        </Button>
      </div>
    </div>
  );
};

export default AccountsFilters;
